'use client';

import Image from 'next/image';

import { useTrackEventAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../../design-system/components/form/button/Button';
import { RATIO, Ratio } from '../../../../../design-system/components/ratio/Ratio';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import {
    ImageAndTextCardType,
    TwoImagesAndTextBlockSliceType,
} from '../../../../../domains/Prismic/BrandPages/BrandPagesDocument';

import style from './TwoImagesAndTextBlockSlice.module.css';

export type TwoImagesAndTextBlockSliceProps = TwoImagesAndTextBlockSliceType;

export function TwoImagesAndTextBlockSlice({
    version,
    firstCard,
    secondCard,
    sliceIndex,
}: TwoImagesAndTextBlockSliceProps & { sliceIndex?: number }) {
    const { trackEvent } = useTrackEventAmplitude();
    return (
        <div className={style.wrapper}>
            <ImageAndTextCard
                version={version}
                title={firstCard.title}
                picture={firstCard.picture}
                text={firstCard.text}
                ctaLabel={firstCard.ctaLabel}
                ctaLink={firstCard.ctaLink}
                target={firstCard.target}
                onClick={() =>
                    trackEvent((ampli, defaultProperties) => {
                        ampli.imageTextBlockClicked({
                            ...defaultProperties,
                            card_number: 1,
                            block_index: sliceIndex ? sliceIndex + 1 : 0,
                            card_title: firstCard.title ?? '',
                        });
                    })
                }
            />
            <ImageAndTextCard
                version={version}
                title={secondCard.title}
                picture={secondCard.picture}
                text={secondCard.text}
                ctaLabel={secondCard.ctaLabel}
                ctaLink={secondCard.ctaLink}
                target={secondCard.target}
                onClick={() =>
                    trackEvent((ampli, defaultProperties) => {
                        ampli.imageTextBlockClicked({
                            ...defaultProperties,
                            card_number: 2,
                            block_index: sliceIndex ? sliceIndex + 1 : 0,
                            card_title: secondCard.title ?? '',
                        });
                    })
                }
            />
        </div>
    );
}

type ImageAndTextCardProps = ImageAndTextCardType & {
    version: TwoImagesAndTextBlockSliceType['version'];
    onClick: () => void;
};

function ImageAndTextCard({
    title,
    picture,
    text,
    ctaLabel,
    ctaLink,
    version,
    onClick,
    target,
}: ImageAndTextCardProps) {
    return (
        <div className={style.card}>
            {picture?.url ? (
                <div className={style.imageContainer}>
                    <Ratio ratio={RATIO['16/9']}>
                        <Image
                            className={style.image}
                            alt={picture.alt}
                            src={picture.url}
                            unoptimized
                            fill
                            loading="lazy"
                        />
                    </Ratio>
                    <div className={style.title}>
                        <Typography as="h2" scale="secondary-m-bold">
                            {title}
                        </Typography>
                    </div>
                </div>
            ) : null}
            <Typography as="div" scale="primary-body-regular">
                {text}
            </Typography>
            {ctaLabel && ctaLink?.url ? (
                <div className={style.cta}>
                    <Button
                        priority="text"
                        reversed={version === 'green'}
                        href={ctaLink?.url}
                        onClick={onClick}
                        target={target || undefined}
                    >
                        {ctaLabel}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
